import React, { useCallback, useEffect, useState } from 'react';
import { processDataForPromoteTable } from '../ARSHelper';
import { getLiveModels } from '../ARSServices/ARSServices';
import CompatioLoader from 'Components/Common/components/CompatioLoader';
import { RecommenderLogsResponse } from '../Types';

type TPromoteTableProps = {
    selectedItems: RecommenderLogsResponse[];
    selectedApplets: AppletPayload;
    setSelectedApplets: React.Dispatch<React.SetStateAction<AppletPayload>>;
    channelKey: string;
    onChangesMade: (changesMade: boolean) => void;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    loading: boolean;
    setAppletsInABTesting: React.Dispatch<React.SetStateAction<string[]>>
    liveModelsData: LiveModelApiResponse | null;
    setLiveModelsData: React.Dispatch<React.SetStateAction<LiveModelApiResponse | null>>
    createInitialSelectedApplets: (liveModelsData: LiveModelApiResponse, channelKey: string) => AppletPayload
}

const PromoteTable = ({ selectedItems, selectedApplets, setSelectedApplets, channelKey, onChangesMade, setLoading, loading, setAppletsInABTesting, liveModelsData, setLiveModelsData, createInitialSelectedApplets }: TPromoteTableProps) => {
    const [models, setModels] = useState<Model[]>([]);
    const [appletNames, setAppletNames] = useState<{ [key: string]: string }>({});
    const [appletsInTest, setAppletsInTest] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        fetchLiveModels();
    }, [selectedItems, channelKey]);

    useEffect(() => {
        checkForChanges();
    }, [selectedApplets, liveModelsData]);

    const fetchLiveModels = async () => {
        try {
            setLoading(true);
            setModels([]);
            const response = await getLiveModels(channelKey);
            if (response?.done && response?.data) {
                const liveModelsData: LiveModelApiResponse = response.data;
                setLiveModelsData(liveModelsData);
                handleSuccessfulResponse(liveModelsData);
            } else {
                handleEmptyResponse();
            }
        } catch (error) {
            console.log("fetchLiveModels ~ error:", error);
            handleEmptyResponse();
        }
    };

    const handleSuccessfulResponse = (liveModelsData: LiveModelApiResponse) => {
        const { models: processedModels, appletNames, appletsInABTesting } = processDataForPromoteTable(liveModelsData, selectedItems);
        setModels(processedModels);
        setAppletNames(appletNames);
        setAppletsInTest(appletsInABTesting)
        const appletsInTestingNames = Object.keys(appletsInABTesting)
            .filter(key => appletsInABTesting[key])
            .map(key => appletNames[key]);
        setAppletsInABTesting(appletsInTestingNames)
        const initialSelectedApplets = createInitialSelectedApplets(liveModelsData, channelKey);
        setSelectedApplets(initialSelectedApplets);
        setLoading(false);
    };

    const handleEmptyResponse = () => {
        setModels([]);
        setSelectedApplets({});
        setLoading(false);
    };

    const handleRadioChange = (modelIndex: number, applet: string) => {
        const selectedModel = getUniqueModels(models)[modelIndex];

        setSelectedApplets(prev => {
            const currentApplet = prev[applet];
            const currentSelectedModelId = currentApplet?.selectedModelId;
            if (currentSelectedModelId === selectedModel.id) {
                const updatedApplet = {
                    ...currentApplet,
                    models: [],
                    selectedModelId: ''
                };
                return {
                    ...prev,
                    [applet]: updatedApplet
                };
            } else {
                const updatedApplet = {
                    ...currentApplet,
                    models: [{ name: selectedModel.name, id: selectedModel.id }],
                    selectedModelId: selectedModel.id
                };
                return {
                    ...prev,
                    [applet]: updatedApplet
                };
            }
        });
    };

    const checkForChanges = useCallback(() => {
        if (!liveModelsData) return false;
        const initialState = createInitialSelectedApplets(liveModelsData, channelKey);
        const appletKeys = Object.keys(liveModelsData).filter(key =>
            !liveModelsData[key]?.ab_testing_enabled
        );
        const changesMade = appletKeys.some(applet => {
            const initialModelId = initialState[applet]?.selectedModelId || '';
            const currentModelId = selectedApplets[applet]?.selectedModelId || '';
            return initialModelId !== currentModelId;
        });
        onChangesMade(changesMade);
    }, [liveModelsData, selectedApplets, channelKey, createInitialSelectedApplets]);

    if (loading) {
        return <CompatioLoader />
    }

    const getUniqueModels = (models: Model[]): Model[] => {
        const uniqueModelsMap = new Map<string, Model>();
        models.forEach((model) => {
            if (uniqueModelsMap.has(model.id)) {
                const existingModel = uniqueModelsMap.get(model.id)!;
                const liveApplets = new Set<string>(existingModel.liveApplets || []);
                if (existingModel.liveApplet) liveApplets.add(existingModel.liveApplet);
                if (model.liveApplet) liveApplets.add(model.liveApplet);
                uniqueModelsMap.set(model.id, {
                    ...existingModel,
                    liveApplets: Array.from(liveApplets)
                });
            } else {
                uniqueModelsMap.set(model.id, {
                    ...model,
                    liveApplets: model.liveApplet ? [model.liveApplet] : []
                });
            }
        });
        return Array.from(uniqueModelsMap.values());
    };

    return (
        <div className='tw-mt-3'>
            {models.length > 0 ? (
                <table className="tw-w-full tw-border-separate tw-border-spacing-0 tw-table-fixed">
                    <colgroup>
                        {models.map((e, index) => <col key={index} className={`tw-w-1/${models.length}+1`} />)}
                    </colgroup>
                    <thead>
                        {<tr>
                            <th className="tw-p-2 tw-text-left tw-text-[#6D788C] tw-font-normal tw-text-sm tw-bg-white">Models List</th>
                            {Object.keys(appletNames).map((appletKey) => {
                                const isInTest = appletsInTest[appletKey];
                                return (<th key={appletKey} className={`tw-p-2 tw-text-center tw-text-[#172B4D] tw-text-sm tw-rounded-t-[10px] tw-bg-[#D9E8FF] tw-border tw-border-[#C9CCCF] tw-border-b-0 ${isInTest ? 'tw-pointer-events-none  tw-select-none tw-bg-[#DADBDD] tw-opacity-80' : ''}`}>
                                    <span className='tw-text-sm tw-text-[#6D788C] tw-font-normal'>{appletNames[appletKey]}</span><br />
                                    <span className='tw-text-[#172B4D] tw-text-sm tw-font-medium tw-truncate tw-block' title={appletKey}>{appletKey}</span>
                                </th>);
                            })}
                        </tr>}
                    </thead>
                    <tbody>
                        {getUniqueModels(models).map((model, index) => (
                            <tr key={index}>
                                <td className="tw-p-2 tw-truncate" title={model.name}>{model.name}</td>
                                {Object.keys(appletNames).map((applet) => {
                                    const isLive = model.liveApplets?.includes(applet) || false;
                                    const isNotLiveAnywhere = model.liveApplet === null;
                                    const isDisabled = appletsInTest[applet] || !isNotLiveAnywhere;
                                    return (
                                        <td
                                            key={applet}
                                            className={`tw-border tw-border-[#C9CCCF] tw-py-1.5 tw-text-center 
                      ${index === 0 ? 'tw-border-t-0' : ''} 
                      ${isLive ? 'tw-bg-[#F4F5F7]' : ''} 
                      ${isDisabled ? 'tw-pointer-events-none tw-select-none tw-bg-[#F4F5F7]' : ''}`}
                                        >
                                            {isLive ? (
                                                <span
                                                    className={`tw-px-2 tw-py-1 tw-border tw-border-[#BCE6BB] tw-text-xs tw-rounded 
                          tw-inline-block tw-truncate tw-max-w-full 
                          ${!appletsInTest[applet]
                                                            ? 'tw-bg-[#E4FBE3] tw-text-[#AEDBAC]'
                                                            : 'tw-bg-[#cfffcd] tw-text-[#11980C]'}`}
                                                    title="Live"
                                                >
                                                    Live
                                                </span>
                                            ) : (
                                                <div
                                                    onClick={() => handleRadioChange(index, applet)}
                                                    className={`tw-cursor-pointer ${isDisabled ? 'tw-pointer-events-none' : ''}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        checked={selectedApplets[applet]?.selectedModelId === model.id}
                                                        className={`tw-form-radio tw-h-4 tw-w-4 tw-text-blue-600 tw-cursor-pointer ${isDisabled ? 'tw-bg-[#F4F5F7] tw-opacity-50' : ''}`}
                                                        disabled={isDisabled}
                                                        readOnly
                                                        aria-disabled
                                                    />
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div>No data found</div>
            )}
        </div>
    );
};

export default PromoteTable;